import React, { useEffect, useState } from 'react';
import { 
  Typography, 
  Container, 
  Paper, 
  TextField, 
  Button, 
  Box,
  Alert,
  CircularProgress,
  styled,
  Chip
} from '@mui/material';
import { Check, Edit } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { shippixService } from '../../services/shippixService';
import { ShippixCredentials } from '../../types/shippix';

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  marginTop: theme.spacing(3),
  background: '#ffffff',
  borderRadius: '8px',
  boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  '& .MuiOutlinedInput-root': {
    '&:hover fieldset': {
      borderColor: '#6B7280',
    },
    '& fieldset': {
      borderColor: '#E5E7EB',
    },
  },
}));

const StyledButton = styled(Button)(({ theme }) => ({
  background: '#1F2937',
  color: '#ffffff',
  padding: '12px 24px',
  '&:hover': {
    background: '#374151',
  },
}));

const StyledChip = styled(Chip)(({ theme }) => ({
  backgroundColor: '#059669',
  color: '#ffffff',
  fontWeight: 500,
  '& .MuiChip-icon': {
    color: '#ffffff'
  }
}));

const DEFAULT_CREDENTIALS: ShippixCredentials = {
  grant_type: 'password',
  client_id: '',
  client_secret: '',
  username: '',
  password: ''
};

export default function ShippingSettings() {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState<ShippixCredentials>(DEFAULT_CREDENTIALS);
  const [isConnected, setIsConnected] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    if (!currentUser?.companyId) return;
    
    try {
      const config = await shippixService.getConfig(currentUser.companyId);
      if (config) {
        setFormData(config);
        setIsConnected(true);
      }
    } catch (error) {
      setError('Erro ao carregar configurações');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUser?.companyId) return;

    setSaving(true);
    setSuccess(false);
    setError('');

    try {
      await shippixService.saveConfig(currentUser.companyId, formData);
      setSuccess(true);
      setIsConnected(true);
      setIsEditing(false);
    } catch (error) {
      setError('Erro ao salvar configurações');
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field: keyof ShippixCredentials) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: e.target.value
    }));
  };

  const handleEdit = () => {
    setIsEditing(true);
    setSuccess(false);
  };

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box display="flex" justifyContent="center" mt={4}>
          <CircularProgress sx={{ color: '#1F2937' }} />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <StyledPaper className="bg-white">
        <Box display="flex" flexDirection="column" alignItems="center" mb={6}>
          <img 
            src="https://portal.shippix.pt/assets/img/logo/logo.svg" 
            alt="Shippix Logo" 
            style={{ height: 50, marginBottom: '24px' }}
          />
          <Typography variant="h5" component="h1" sx={{ color: '#1F2937', fontWeight: 500, mb: 2 }}>
            Configurações do Shippix
          </Typography>
          {isConnected && !isEditing && (
            <StyledChip 
              icon={<Check size={16} />}
              label="Conectado" 
            />
          )}
        </Box>

        {success && (
          <Alert severity="success" sx={{ mb: 4 }}>
            Configurações salvas com sucesso!
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 4 }}>
            {error}
          </Alert>
        )}

        {isConnected && !isEditing ? (
          <Box display="flex" justifyContent="center">
            <StyledButton
              startIcon={<Edit size={18} />}
              onClick={handleEdit}
              variant="outlined"
              sx={{
                background: 'transparent',
                border: '1px solid #1F2937',
                color: '#1F2937',
                '&:hover': {
                  background: '#F3F4F6',
                  border: '1px solid #1F2937',
                }
              }}
            >
              Editar Configurações
            </StyledButton>
          </Box>
        ) : (
          <form onSubmit={handleSubmit}>
            <StyledTextField
              fullWidth
              label="Client ID"
              value={formData.client_id}
              onChange={handleChange('client_id')}
              required
              placeholder="Insira o Client ID"
            />
            
            <StyledTextField
              fullWidth
              label="Client Secret"
              value={formData.client_secret}
              onChange={handleChange('client_secret')}
              required
              placeholder="Insira o Client Secret"
            />
            
            <StyledTextField
              fullWidth
              label="Username"
              value={formData.username}
              onChange={handleChange('username')}
              required
              placeholder="Insira o Username"
            />
            
            <StyledTextField
              fullWidth
              type="password"
              label="Password"
              value={formData.password}
              onChange={handleChange('password')}
              required
              placeholder="Insira a Password"
            />

            <Box display="flex" justifyContent="center" mt={4}>
              {isEditing && (
                <Button
                  variant="outlined"
                  onClick={() => setIsEditing(false)}
                  sx={{ 
                    mr: 2,
                    borderColor: '#6B7280',
                    color: '#6B7280',
                    '&:hover': {
                      borderColor: '#4B5563',
                      background: '#F9FAFB'
                    }
                  }}
                >
                  Cancelar
                </Button>
              )}
              <StyledButton 
                variant="contained" 
                type="submit"
                disabled={saving}
                size="large"
                className="bg-gray-900 hover:bg-gray-800"
              >
                {saving ? <CircularProgress size={24} sx={{ color: '#ffffff' }} /> : 'Guardar Configurações'}
              </StyledButton>
            </Box>
          </form>
        )}
      </StyledPaper>
    </Container>
  );
}
